import { useDispatch, useSelector } from "react-redux";
import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { setTitleBar } from "store/titleBar.slice";
import "./activities.scss";
import { useTranslation } from "react-i18next";
import { debounce, isEqual } from 'lodash';
import { useWebsocketsService } from "hook/useWebsockets";
import Loader from "components/loader/loader";
import type { activityType } from "models/activity.model";
import { useScrollableSections } from "hook/useScrollableSections";
import ScrollableSection, { type sectionDataType } from "components/scrollableSection/scrollableSection";
import { ResourcesService } from "services/resources.service";

const Activities = () => {

  const dispatch = useDispatch();
  const { t, i18n } = useTranslation();
  const { lastMessage } = useWebsocketsService();
  const resourcesService = new ResourcesService();
  const location = useLocation();
  const [filteredData, setFilteredData] = useState<{ [k: string]: sectionDataType }>({});
  const [isLoading, setIsLoading] = useState(true);
  const searchBarFilteredData: activityType[] = useSelector((state: any) => state.titleBar.filteredData);
  const theme = useSelector((state: any) => state.theme);
  const prevDataRef = useRef(filteredData);
  const token = sessionStorage.getItem('accessToken');

  const {
    seeMore,
    snapScroll,
    checkScrollableSections,
    containersRef,
  } = useScrollableSections(filteredData, setFilteredData);

  const fetchData = async () => {
    const data = await resourcesService.loadResourcesByPath(location.pathname);
    dispatch(setTitleBar({
      title: data.titleKey,
      data: data.data || [],
      filteredData: data.data || [],
      isBackButton: false,
      isSearchBar: true,
      overrideSearchFn: false,
    }));
    setIsLoading(false);
  }

  const createSections = (activities: activityType[]) => {
    const safeActivities = Array.isArray(activities) ? activities : [];
    const activitiesCopy: activityType[] = safeActivities.map(item => ({
      ...item,
      week: item.week, 
      date: item.date,
      longDate: item.longDate,
    }));

    return activitiesCopy?.reduce<Record<string, sectionDataType>>((accumulator, currenValue) => {
      if (!currenValue.week) {
        console.warn('week manquant pour l\'activité', currenValue);
        return accumulator;
      }

      const group = currenValue.week.weekNumber.toString();
      accumulator[group] = accumulator[group] || { 
        sectionId: currenValue.week.weekNumber, 
        sectionName: `${t("from")} ${currenValue.week.label.firstDayWeek} ${t("to")} ${currenValue.week.label.lastDayWeek}`,
        activities: [], 
        isSeeMore: false, 
        isScrollable: false, 
        isLoading: false
      };

      accumulator[group].activities.push(currenValue);
      return accumulator;
    }, {});
  }

  useEffect(() => {
    const handleResize = debounce(() => {
      checkScrollableSections();
    }, 300);

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      dispatch(setTitleBar({ title: "", data: [], filteredData: [] }));
    }
  }, []);

  useEffect(() => {
    if (!token) return;

    fetchData();
    setIsLoading(true);

  }, [token, location.pathname]);

  useEffect(() => {
    if (!isEqual(prevDataRef.current, filteredData)) {
      checkScrollableSections();
      prevDataRef.current = filteredData;
    }
  }, [filteredData]);


  useEffect(() => {
    let filteredDataFormated= createSections(searchBarFilteredData);
    setFilteredData(filteredDataFormated);

  }, [searchBarFilteredData, i18n.language])

  useEffect(() => {
    if (lastMessage) {
      const { id } = JSON.parse(lastMessage.data);

      if (id === "playerClosed") {
        fetchData();
        setIsLoading(true);
      }
    }
  }, [lastMessage]);


  return (
    <div className="activities">
      {isLoading ? (
        <Loader className={"loader_tertiary"} />
      ) : (
        filteredData && Object.keys(filteredData).length > 0 ? (
          Object.keys(filteredData).map((key, idx) => {

            const weekSection = filteredData[key as keyof typeof filteredData];

            return (
              <ScrollableSection
                ref={(el) => { containersRef.current[weekSection.sectionId] = el }}
                key={idx}
                id={weekSection.sectionId}
                idx={idx}
                title={weekSection.sectionName}
                isScrollable={weekSection.isScrollable}
                isSeeMore={weekSection.isSeeMore}
                items={weekSection.activities}
                onClose={fetchData}
                onSeeMoreToggle={() => seeMore(weekSection.sectionId)}
                onSnapScroll={snapScroll}
                isMobile={theme.isTabletPortraitOrMobile}
                isLoading={weekSection.isLoading}
              />

            )
          }
          )
        ) : (
          <div className="noActivities centered">
            {t("noActivity")}
          </div>
        )
      )}
    </div>
  );
};

export default Activities;
